import type { CartProductInterface } from '@/types'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'
import { withVat } from '@/utils/price/vat'
import getProductKey from '@/utils/products/getProductKey'

import type { CartTotals } from './cartServiceTypes'
import { cartValidationService } from './CartValidationService'

export class CartCalculationService {
  private getSellingPrice(product: CartProductInterface): number | null {
    return product.prices?.sellingPrice ?? null
  }

  private getRetailPrice(product: CartProductInterface): number | null {
    return product.prices?.retailPrice ?? null
  }

  calculateTotals(
    products: CartProductInterface[],
    quantities: Record<string, number>
  ): CartTotals {
    if (!products.length) {
      return { totalOpt: 0, totalRetail: 0 }
    }

    let totalOpt = 0
    let totalRetail = 0

    products.forEach((product) => {
      const keyForQuantities = getProductKey(product)
      const quantity = quantities[String(keyForQuantities)] ?? 0
      if (quantity === 0) return

      const sellingPrice = this.getSellingPrice(product)
      if (sellingPrice !== null) {
        totalOpt += withVat(quantity * sellingPrice)
      }

      const retailPrice = this.getRetailPrice(product)
      if (retailPrice !== null) {
        totalRetail += withVat(quantity * retailPrice)
      }
    })

    return { totalOpt, totalRetail }
  }

  formatPrice(price: number): string {
    return trimTrailingZeros(price)
  }

  calculateProductTotal(
    product: CartProductInterface,
    quantity: number
  ): number {
    const sellingPrice = this.getSellingPrice(product)
    if (sellingPrice === null) return 0
    return withVat(sellingPrice * quantity)
  }

  calculateProductRetailTotal(
    product: CartProductInterface,
    quantity: number
  ): number {
    const retailPrice = this.getRetailPrice(product)
    if (retailPrice === null) return 0
    return withVat(retailPrice * quantity)
  }

  getProductPriceWithVAT(product: CartProductInterface): string {
    const sellingPrice = this.getSellingPrice(product)
    if (sellingPrice === null) {
      const status = product.availabilityStatus
      return !status || status === 'AVAILABLE' ? 'Товар не расценен' : '-'
    }
    return this.formatPrice(withVat(sellingPrice))
  }

  getProductRetailPriceWithVAT(product: CartProductInterface): string {
    const retailPrice = this.getRetailPrice(product)
    if (retailPrice === null) return '-'
    return this.formatPrice(withVat(retailPrice))
  }

  getSummaryTotals(
    productsChecked: CartProductInterface[],
    duplicateProductsQuantities: Record<string, number>
  ): { totalOpt: string; totalRetail: string; selectedCount: number } {
    const selectedCount = productsChecked.length

    if (!selectedCount) {
      return { totalOpt: '-', totalRetail: '-', selectedCount: 0 }
    }

    const totals = this.calculateTotals(
      productsChecked,
      duplicateProductsQuantities
    )

    return {
      totalOpt: this.formatPrice(totals.totalOpt),
      totalRetail: this.formatPrice(totals.totalRetail),
      selectedCount,
    }
  }

  getUnorderableTotals(
    productsChecked: CartProductInterface[],
    duplicateProductsQuantities: Record<string, number>
  ): { totalOpt: string; totalRetail: string } | null {
    const unorderable = productsChecked.filter(
      (product) => !cartValidationService.isProductAvailable(product)
    )
    if (unorderable.length === 0) return null

    const totals = this.calculateTotals(
      unorderable,
      duplicateProductsQuantities
    )
    return {
      totalOpt: this.formatPrice(totals.totalOpt),
      totalRetail: this.formatPrice(totals.totalRetail),
    }
  }
}

export const cartCalculationService = new CartCalculationService()
